/**
 * Guide copy for the research, ads, market, and Naver tools. The shape and the
 * reason this file exists are documented on `toolGuide.ts`.
 */

import type { ToolGuide } from './toolGuideShape';

export const TOOL_GUIDE_RESEARCH: Readonly<Record<string, ToolGuide>> = {
  'youtube-comment-analyzer': {
    fields: [
      'Question and term counts',
      'Ranked comment threads',
      'Themes with sentiment',
      'The comment behind every theme',
    ],
    steps: [
      'Paste the watch URL of the video whose audience you want to read.',
      'Run it and let the public comment sample load.',
      'Open a theme to see the comments that produced it.'],
    limits: [
      ['Accepts', 'One public YouTube watch URL per run.'],
      ['Returns', 'Questions, recurring terms, and themes, each cited to a comment.'],
      ['Stops at', 'The sample that came back, whose row count is printed rather than implied.'],
    ],
  },
  'reddit-research': {
    steps: [
      'Type the phrase buyers would actually use, such as switching from Notion.',
      'Choose the sort and the timeframe so you know which slice was searched.',
      'Read the synthesis, then open the permalink behind any line you doubt.'],
    limits: [
      ['Accepts', 'A search phrase, a sort order, and a timeframe.'],
      ['Returns', 'Matching public threads plus a synthesis tied back to them.'],
      ['Stops at', 'Private subreddits and anything a logged-out reader cannot see.'],
    ],
  },
  'reddit-marketing': {
    steps: [
      'Paste your product URL so the planner can read what you actually sell.',
      'Run it to retrieve the threads that surface for that context.',
      'Work the suggested communities and angles, checking each citation first.'],
    limits: [
      ['Accepts', 'A public product or company URL.'],
      ['Returns', 'Communities and posting angles, each cited to a retrieved thread.'],
      ['Stops at', 'Posting: subreddit rules and disclosure remain yours.'],
    ],
  },
  'brand-mention-checker': {
    steps: [
      'Enter the brand or product name people would type, such as Notion.',
      'Run it against recent public Reddit posts and comments.',
      'Open each mention at its source before you act on it.'],
    limits: [
      ['Accepts', 'One brand or keyword string per run.'],
      ['Returns', 'Recent public mentions with a direct link on every row.'],
      ['Stops at', 'A single point in time, with the retrieval moment stated.'],
    ],
  },
  'switch-signals': {
    steps: [
      'Enter the competitor whose customers you want to hear from.',
      'Run it to find posts about leaving, replacing, or shopping around.',
      'Judge each row yourself from the thread it links to.'],
    limits: [
      ['Accepts', 'Any competitor name people actually write.'],
      ['Returns', 'Public posts matching switching language for that name.'],
      ['Stops at', 'Certainty about intent, which stays a human read.'],
    ],
  },
  'ask-social-data': {
    steps: [
      'Write the question in plain English, such as a follower count for a named account.',
      'Run it and see which single operation the question mapped to.',
      'Read the typed answer, and reproduce it with the same call if you want to.'],
    limits: [
      ['Accepts', 'One question at a time, in ordinary language.'],
      ['Returns', 'Typed data plus the operation that produced it.'],
      ['Stops at', 'Questions that map to nothing, which are refused rather than guessed.'],
    ],
  },
  'facebook-ad-library': {
    fields: [
      'Each ad normalized to one field set',
      'Its advertiser and country',
      'A link to the public library entry',
    ],
    steps: [
      'Enter the advertiser or keyword you want to see running, such as Nike.',
      'Set the country and whether you only want ads currently live.',
      'Open any ad at its public library entry.'],
    limits: [
      ['Accepts', 'A brand or keyword, a country, and an active filter.'],
      ['Returns', 'Public ads normalized to one field set, each with its library link.'],
      ['Stops at', 'Spend and performance, which Meta publishes for political ads only.'],
    ],
  },
  'amazon-review-signals': {
    fields: [
      'Sample size',
      'Verified purchases',
      'Largest day cluster',
      'Exact duplicate texts',
      'Rating distribution',
    ],
    steps: [
      'Paste an ASIN such as B07PXGQC1Q, or the product URL it sits on.',
      'Run it to pull the public review sample.',
      'Read sample size, verified share, the largest day cluster, and duplicate text together.'],
    limits: [
      ['Accepts', 'One ASIN or Amazon product URL per run.'],
      ['Returns', 'Four counted signals over the sample it retrieved.'],
      ['Stops at', 'A verdict, which stays with you rather than a score.'],
    ],
  },
  'app-review-analyzer': {
    fields: [
      'Returned reviews',
      'Average rating',
      'Rating spread across both stores',
      'Recurring review words',
      'Weekly review volume',
    ],
    steps: [
      'Enter a Play package name such as com.whatsapp, or an App Store numeric id.',
      'Run it to pull the public review sample for that listing.',
      'Compare rating spread, recurring words, and weekly volume across both stores.'],
    limits: [
      ['Accepts', 'One store listing identifier per run.'],
      ['Returns', 'Rating distribution, review vocabulary, and volume over time.'],
      ['Stops at', 'Installs and revenue, which no store exposes publicly.'],
    ],
  },
  'stock-comparison': {
    fields: [
      'Price and day range',
      '52-week range',
      'Volume',
      'Market cap',
      'Market state',
    ],
    steps: [
      'Enter the tickers you want side by side, such as AAPL, MSFT.',
      'Run it to pull one quote per ticker.',
      'Compare the same fields down the column, with the retrieval time attached.'],
    limits: [
      ['Accepts', 'A comma-separated list of tickers.'],
      ['Returns', 'Price, day and 52-week ranges, volume, market cap, and market state.'],
      ['Stops at', 'Any judgement about what the numbers mean.'],
    ],
  },
  'naver-blog-analytics': {
    fields: [
      'Raw documents and returned rows',
      'Distinct bloggers',
      'Competition band',
      'Share published within 90 days',
      'Highest matching position, when you give a blog ID',
    ],
    steps: [
      'Enter the Korean search keyword you want to rank for.',
      'Add your blog ID if you want your own position located in the results.',
      'Read competition band, blogger concentration, and freshness before you write.'],
    limits: [
      ['Accepts', 'One keyword, and optionally one blog ID.'],
      ['Returns', 'Raw and returned document counts, distinct bloggers, and recency share.'],
      ['Stops at', 'Owner analytics, which live behind a Naver login.'],
    ],
  },
  'reddit-seo-finder': {
    fields: [
      'The buyer-intent keywords read off your site',
      'Each ranking Reddit thread',
      'The exact query that surfaced it',
      'An inspectable search link per row',
    ],
    steps: [
      'Paste your website, such as yourcompany.com.',
      'Review the buyer-intent keywords it reads off your site, and edit them.',
      'Open the Reddit threads already ranking for those searches.'],
    limits: [
      ['Accepts', 'A public website URL, plus any keywords you add yourself.'],
      ['Returns', 'Ranking Reddit threads, each tied to the exact query that found it.'],
      ['Stops at', 'Commenting, which is yours to do well or not at all.'],
    ],
  },
};
